import type { PlayerAnimationSource } from '../types';
import type { ActivePlaybackRange } from './activePlaybackRanges';
import { isTimelineFrameWithinActivePlaybackRanges } from './activePlaybackRanges';
import { hasCompleteScheduledSequencePlayback } from './sequencePlaybackSchedule';

export function hasScheduledActivePlaybackRangeMask(
  animation: PlayerAnimationSource | undefined,
): boolean {
  if (animation?.type !== 'sequence') {
    return false;
  }

  if (!hasCompleteScheduledSequencePlayback(animation, animation.urls.length)) {
    return false;
  }

  return Boolean(animation.activePlaybackRanges?.length);
}

export function mapPlayerFrameToTimelineFrame(
  playerFrame: number,
  playerFrameRate: number,
  timelineFrameRate?: number,
): number {
  const normalizedPlayerFrameRate =
    Number.isFinite(playerFrameRate) && playerFrameRate > 0
      ? playerFrameRate
      : 24;
  const normalizedTimelineFrameRate =
    timelineFrameRate !== undefined &&
    Number.isFinite(timelineFrameRate) &&
    timelineFrameRate > 0
      ? timelineFrameRate
      : normalizedPlayerFrameRate;

  if (!Number.isFinite(playerFrame)) {
    return 0;
  }

  if (normalizedTimelineFrameRate === normalizedPlayerFrameRate) {
    return Math.round(playerFrame);
  }

  return Math.round(
    (Math.round(playerFrame) / normalizedPlayerFrameRate) *
      normalizedTimelineFrameRate,
  );
}

export function resolveTimelineGapPlaybackVisibility(input: {
  animation?: PlayerAnimationSource;
  currentFrame: number;
  playerFrameRate: number;
}): boolean {
  const { animation, currentFrame, playerFrameRate } = input;

  if (!hasScheduledActivePlaybackRangeMask(animation)) {
    return true;
  }

  const activePlaybackRanges = (
    animation as Extract<PlayerAnimationSource, { type: 'sequence' }>
  ).activePlaybackRanges!;
  const timelineFrameRate =
    animation?.type === 'sequence' ? animation.timelineFrameRate : undefined;
  const timelineFrame = mapPlayerFrameToTimelineFrame(
    currentFrame,
    playerFrameRate,
    timelineFrameRate,
  );

  return isTimelineFrameWithinActivePlaybackRanges(
    timelineFrame,
    activePlaybackRanges,
  );
}

export function resolveTimelinePlaceholderTransitionGhostMode(input: {
  animation?: PlayerAnimationSource;
  currentFrame: number;
  playerFrameRate: number;
}): boolean {
  const { animation, currentFrame, playerFrameRate } = input;

  if (!hasScheduledActivePlaybackRangeMask(animation)) {
    return false;
  }

  return !resolveTimelineGapPlaybackVisibility(input);
}

export function resolveEffectiveCharacterVisibility(input: {
  hasAnimationSource: boolean;
  characterSceneReady: boolean;
  timelineGapVisible: boolean;
  baseActorVisible?: boolean;
}): boolean {
  const {
    hasAnimationSource,
    characterSceneReady,
    timelineGapVisible,
    baseActorVisible = true,
  } = input;

  return (
    baseActorVisible &&
    (!hasAnimationSource || characterSceneReady) &&
    timelineGapVisible
  );
}

export type { ActivePlaybackRange };
